import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import Spinner from "../components/spinner";
import BILLING from "../api/services/BILLING";
import { paths } from "../routes/paths";
import styles from "../styles/_screens/allMemberships.module.scss";

const FILTER_TABS = [
  { key: "all", label: "\u05D4\u05DB\u05DC" },
  { key: "active", label: "\u05E4\u05E2\u05D9\u05DC" },
  { key: "frozen", label: "\u05DE\u05D5\u05E7\u05E4\u05D0" },
  { key: "expired", label: "\u05E4\u05D2 \u05EA\u05D5\u05E7\u05E3" },
  { key: "exhausted", label: "\u05E0\u05D5\u05E6\u05DC" },
];

const PAGE_SIZE = 20;

export default function AllMemberships() {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const debounceRef = useRef(null);

  // ── Fetch memberships ──
  const fetchMemberships = useCallback(
    async (currentPage, currentFilter, currentSearch, append = false) => {
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const params = {
          page: currentPage,
          size: PAGE_SIZE,
          search: currentSearch || undefined,
          status: currentFilter !== "all" ? currentFilter : undefined,
        };
        const res = await BILLING.get_all_memberships(params);
        const data = res?.data;
        const items = Array.isArray(data)
          ? data
          : data?.items || data?.memberships || data?.data || [];
        const total = data?.total;

        if (append) {
          setMemberships((prev) => [...prev, ...items]);
        } else {
          setMemberships(items);
        }

        if (total != null) {
          setHasMore(currentPage * PAGE_SIZE < total);
        } else {
          setHasMore(items.length >= PAGE_SIZE);
        }
      } catch (error) {
        console.error(error);
        toast.error(
          error?.response?.data?.message || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E0\u05EA \u05DE\u05E0\u05D5\u05D9\u05D9\u05DD"
        );
        if (!append) setMemberships([]);
      }
      setLoading(false);
      setLoadingMore(false);
    },
    []
  );

  // Trigger fetch on filter / page changes
  useEffect(() => {
    if (page === 1) {
      fetchMemberships(1, filter, search, false);
    } else {
      fetchMemberships(page, filter, search, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchMemberships(1, filter, search, false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ── Filter change handler ──
  const handleFilterChange = (newFilter) => {
    if (newFilter === filter) return;
    setFilter(newFilter);
    setPage(1);
  };

  // ── Load more ──
  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  // ── Helpers ──
  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy");
    } catch {
      return dateStr || "-";
    }
  };

  const getPlanType = (membership) => {
    if (membership.isSubscription) {
      return { label: "\u05DE\u05E0\u05D5\u05D9 \u05D7\u05D5\u05D3\u05E9\u05D9", className: styles.typeSubscription };
    }
    return { label: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05D4", className: styles.typePunchcard };
  };

  const getStatus = (membership) => {
    if (membership.isFrozen)
      return { label: "\u05DE\u05D5\u05E7\u05E4\u05D0", className: styles.statusFrozen };

    const computed = membership._computed || {};
    if (computed.remaining != null && computed.remaining <= 0)
      return { label: "\u05E0\u05D5\u05E6\u05DC", className: styles.statusExhausted };

    const isExpired =
      membership.expiresAt && new Date(membership.expiresAt) < new Date();
    if (!membership.isActive || isExpired)
      return { label: "\u05E4\u05D2 \u05EA\u05D5\u05E7\u05E3", className: styles.statusExpired };

    return { label: "\u05E4\u05E2\u05D9\u05DC", className: styles.statusActive };
  };

  const getEntries = (membership) => {
    if (membership.isSubscription) return "\u2014";
    const computed = membership._computed || {};
    const remaining = computed.remaining;
    const total = membership.numberOfTurns;
    if (remaining != null && total != null) {
      return `${remaining}/${total}`;
    }
    return "\u2014";
  };

  const getExpiry = (membership) => {
    if (!membership.expiresAt) return "\u05DC\u05DC\u05D0 \u05D4\u05D2\u05D1\u05DC\u05D4";
    return formatDate(membership.expiresAt);
  };

  // ── Loading state ──
  if (loading && memberships.length === 0) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.main}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.main}>
            <div className={styles.tableContainer}>
              {/* Header */}
              <div className={styles.header}>
                <h2>{"\u05DE\u05E0\u05D5\u05D9\u05D9\u05DD \u05E4\u05E2\u05D9\u05DC\u05D9\u05DD \u05D1\u05E2\u05E1\u05E7"}</h2>
              </div>

              {/* Filter tabs */}
              <div className={styles.tabs}>
                {FILTER_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`${styles.tab} ${filter === tab.key ? styles.tabActive : ""}`}
                    onClick={() => handleFilterChange(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className={styles.searchRow}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={"\u05D7\u05D9\u05E4\u05D5\u05E9 \u05DC\u05E4\u05D9 \u05E9\u05DD \u05DC\u05E7\u05D5\u05D7 \u05D0\u05D5 \u05D8\u05DC\u05E4\u05D5\u05DF..."}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  dir="rtl"
                />
              </div>

              {/* Table */}
              {memberships.length === 0 ? (
                <div className={styles.emptyState}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <path d="M16 10a4 4 0 01-8 0" />
                  </svg>
                  <p>{"\u05D0\u05D9\u05DF \u05DE\u05E0\u05D5\u05D9\u05D9\u05DD"}</p>
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th>{"\u05DC\u05E7\u05D5\u05D7"}</th>
                        <th>{"\u05DE\u05E0\u05D5\u05D9/\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05D4"}</th>
                        <th>{"\u05E1\u05D5\u05D2"}</th>
                        <th>{"\u05E1\u05D8\u05D8\u05D5\u05E1"}</th>
                        <th>{"\u05DB\u05E0\u05D9\u05E1\u05D5\u05EA"}</th>
                        <th>{"\u05EA\u05D5\u05E7\u05E3"}</th>
                        <th>{"\u05EA\u05D0\u05E8\u05D9\u05DA \u05E8\u05DB\u05D9\u05E9\u05D4"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberships.map((membership) => {
                        const planType = getPlanType(membership);
                        const status = getStatus(membership);
                        const entries = getEntries(membership);
                        const expiry = getExpiry(membership);
                        const purchaseDate = formatDate(
                          membership.paidAt || membership.createdAt
                        );

                        return (
                          <tr
                            key={membership._id}
                            className={styles.tableRow}
                            onClick={() => {
                              const clientId =
                                membership.userId?._id || membership.userId;
                              if (clientId && typeof clientId === "string") {
                                navigate(
                                  `${paths.clientBilling(clientId)}?tab=purchases`
                                );
                              }
                            }}
                          >
                            <td data-label="\u05DC\u05E7\u05D5\u05D7">
                              <div className={styles.customerCell}>
                                <span className={styles.customerName}>
                                  {membership.userId?.name || "-"}
                                </span>
                                <span className={styles.customerPhone}>
                                  {membership.userId?.phone || ""}
                                </span>
                              </div>
                            </td>
                            <td data-label="\u05DE\u05E0\u05D5\u05D9/\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05D4">
                              {membership.name || "-"}
                            </td>
                            <td data-label="\u05E1\u05D5\u05D2">
                              <span
                                className={`${styles.typeBadge} ${planType.className}`}
                              >
                                {planType.label}
                              </span>
                            </td>
                            <td data-label="\u05E1\u05D8\u05D8\u05D5\u05E1">
                              <span
                                className={`${styles.statusBadge} ${status.className}`}
                              >
                                {status.label}
                              </span>
                            </td>
                            <td
                              data-label="\u05DB\u05E0\u05D9\u05E1\u05D5\u05EA"
                              className={styles.entriesCell}
                            >
                              {entries}
                            </td>
                            <td data-label="\u05EA\u05D5\u05E7\u05E3">{expiry}</td>
                            <td data-label="\u05EA\u05D0\u05E8\u05D9\u05DA \u05E8\u05DB\u05D9\u05E9\u05D4">
                              {purchaseDate}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Load more */}
              {hasMore && memberships.length > 0 && (
                <div className={styles.loadMore}>
                  <button
                    className={styles.loadMoreBtn}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <Spinner /> : "\u05D8\u05E2\u05DF \u05E2\u05D5\u05D3"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
